import React, { useState, useEffect } from 'react';
import { 
  FiShield, FiBriefcase, FiCpu, FiTrendingUp, 
  FiUsers, FiCheckCircle, FiSave, FiCheck, FiFileText 
} from 'react-icons/fi';
import ImageUploadField from './ImageUploadField';
import AdminExhibitors from './AdminExhibitors';
import AdminPartners from './AdminPartners';
import './AdminManage.css';

const STORAGE_KEY_APSIS = 'iamar_apsis_general_content';

const DEFAULT_APSIS_DATA = {
  about: {
    title: "About APSIS 2026",
    subtitle: "Agadir Private Security & Innovation Summit",
    tag: "ÉVÉNEMENT STRATÉGIQUE",
    pdfTitle: "Plaquette Marketing APSIS 2026",
    bannerImage: "",
    description: "Le sommet de référence pour les entreprises de sécurité privée, technologies de surveillance, drones et solutions de protection des actifs critiques en Afrique et dans l'espace méditerranéen."
  },
  securityInnovation: {
    title: "Sécurité & Innovation Technologique",
    summary: "Démonstrations live de solutions de surveillance intelligente, détection d'intrusions par IA et technologies de défense passive."
  },
  startups: {
    title: "Espace Startups Sécurité",
    summary: "Tremplin pour les jeunes pousses innovantes développant des briques technologiques de rupture."
  },
  registration: {
    title: "Inscriptions & Accréditations APSIS 2026",
    infoText: "Ouverture des badges professionnels pour les délégations officielles, exposants et visiteurs qualifiés."
  }
};

export default function AdminAPSISSection() {
  const [activeSubTab, setActiveSubTab] = useState('about');
  const [data, setData] = useState(DEFAULT_APSIS_DATA);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_APSIS);
      if (saved) {
        setData(JSON.parse(saved));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY_APSIS, JSON.stringify(data));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const updateField = (section, field, val) => {
    setData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: val
      }
    }));
  };

  const subTabs = [
    { id: 'about', label: "Présentation APSIS", icon: <FiFileText /> },
    { id: 'exhibitors', label: "Exposants", icon: <FiBriefcase /> },
    { id: 'securityInnovation', label: "Sécurité & Innovation", icon: <FiCpu /> },
    { id: 'startups', label: "Startups", icon: <FiTrendingUp /> },
    { id: 'industrialPartners', label: "Partenaires Industriels", icon: <FiUsers /> },
    { id: 'registration', label: "Inscriptions", icon: <FiCheckCircle /> }
  ];

  return (
    <div className="admin-manage-card" style={{ background: '#ffffff', borderRadius: 8, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 16, marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#1b1e4f', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiShield /> APSIS 2026 (Sommet Sécurité Privée)
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '0.88rem' }}>
            Gestion de l'événement APSIS 2026, liste des exposants, startups et partenaires industriels
          </p>
        </div>

        {['about', 'securityInnovation', 'startups', 'registration'].includes(activeSubTab) && (
          <button
            onClick={handleSave}
            style={{
              background: saveSuccess ? '#10b981' : '#1b1e4f',
              color: 'white',
              border: 'none',
              padding: '9px 20px',
              borderRadius: 6,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {saveSuccess ? <FiCheck size={16} /> : <FiSave size={16} />}
            {saveSuccess ? 'Enregistré avec succès !' : 'Enregistrer les modifications'}
          </button>
        )}
      </div>

      {/* Sub Tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24, padding: 6, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            style={{
              border: 'none',
              background: activeSubTab === tab.id ? '#1b1e4f' : 'transparent',
              color: activeSubTab === tab.id ? '#ffffff' : '#475569',
              padding: '8px 14px',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              transition: 'all 0.15s ease'
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Sub-tab 1: About APSIS */}
      {activeSubTab === 'about' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Présentation du Sommet APSIS 2026
          </h3>

          <ImageUploadField
            label="Bannière principale APSIS (Upload image)"
            value={data.about.bannerImage}
            onChange={(url) => updateField('about', 'bannerImage', url)}
            helpText="Téléversez le visuel d'en-tête pour la page APSIS"
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre principal</label>
              <input
                type="text"
                value={data.about.title}
                onChange={(e) => updateField('about', 'title', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Tag / Badge (ex: ÉVÉNEMENT STRATÉGIQUE)</label>
              <input
                type="text"
                value={data.about.tag}
                onChange={(e) => updateField('about', 'tag', e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Sous-titre descriptif</label>
            <input
              type="text"
              value={data.about.subtitle}
              onChange={(e) => updateField('about', 'subtitle', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Description générale de l'événement</label>
            <textarea
              rows={4}
              value={data.about.description}
              onChange={(e) => updateField('about', 'description', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Sub-tab 2: Exposants */}
      {activeSubTab === 'exhibitors' && <AdminExhibitors />}

      {/* Sub-tab 3: Sécurité & Innovation */}
      {activeSubTab === 'securityInnovation' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Sécurité & Innovation Technologique
          </h3>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre de la section</label>
            <input
              type="text"
              value={data.securityInnovation.title}
              onChange={(e) => updateField('securityInnovation', 'title', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Résumé des innovations présentées</label>
            <textarea
              rows={4}
              value={data.securityInnovation.summary}
              onChange={(e) => updateField('securityInnovation', 'summary', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Sub-tab 4: Startups */}
      {activeSubTab === 'startups' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Espace Startups Sécurité
          </h3>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre de la section</label>
            <input
              type="text"
              value={data.startups.title}
              onChange={(e) => updateField('startups', 'title', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Présentation du programme Startups</label>
            <textarea
              rows={4}
              value={data.startups.summary}
              onChange={(e) => updateField('startups', 'summary', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}

      {/* Sub-tab 5: Partenaires Industriels */}
      {activeSubTab === 'industrialPartners' && <AdminPartners />}

      {/* Sub-tab 6: Inscriptions */}
      {activeSubTab === 'registration' && (
        <div style={{ maxWidth: 840 }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: 16 }}>
            Inscriptions & Billetterie APSIS
          </h3>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Titre</label>
            <input
              type="text"
              value={data.registration.title}
              onChange={(e) => updateField('registration', 'title', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Informations d'inscription</label>
            <textarea
              rows={4}
              value={data.registration.infoText}
              onChange={(e) => updateField('registration', 'infoText', e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
